import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, listOrganizations, resetUserCredentials, approveUser, lockUser, resendWelcomeMail } from '../api';
import { useAuth } from '../context/AuthContext';
import SearchInput from '../components/SearchInput';
import { useSort, sortBy, SortHead } from '../components/PortalUI';
import PortalIcon from '../components/PortalIcon';
import NcIcon from '../components/NcIcon';
import './styles.css';
import '../components/PortalUI.css';
import LoadingState from '../components/LoadingState';
import ConfirmDialog from '../components/ConfirmDialog';

function formatDateTime(iso) {
  if (!iso) return 'Aldrig logget ind';
  return new Date(iso).toLocaleString('da-DK', { dateStyle: 'medium', timeStyle: 'short' });
}

const RESET_LABEL = {
  password: { title: 'Nulstil adgangskode', what: 'den nuværende adgangskode' },
  mfa: { title: 'Nulstil MFA', what: 'den nuværende godkender-app' },
  both: { title: 'Nulstil adgangskode og MFA', what: 'den nuværende adgangskode og godkender-app' },
};

function initials(name) {
  return (name || '?').split(' ').filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

// What the account is: NordComply's own team, an organisation's admin, or a customer's user. What a person may do is separate (rights).
const ROLE_LABEL = { provider: 'NordComply', admin: 'MSP-admin', customer: 'Kundebruger' };
const NC_RIGHTS = [['news', 'Se og skrive Nyheder'], ['licenses', 'Se Licenser'], ['support', 'Skrive Support'], ['delete', 'Slette organisationer og kunder']];

const ICON = {
  search: <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-4.3-4.3" />,
  back: <path d="M19 12H5M12 19l-7-7 7-7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="M5 12l5 5L20 7" />,
};
const Svg = ({ name, size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON[name]}</svg>;

// Segmented choice: white track, the chosen one in a light violet.
function Segmented({ value, options, onChange, disabled }) {
  return (
    <div className="pu-seg pu-seg--w" role="group">
      {options.map(([key, label]) => (
        <button key={key} type="button" className={value === key ? 'is-on' : ''} onClick={() => onChange(key)} disabled={disabled}>{label}</button>
      ))}
    </div>
  );
}

function TenantChecklist({ tenants, selected, onToggle, disabled }) {
  if (tenants.length === 0) return <em>Ingen tenants at vælge mellem endnu.</em>;
  return (
    <div className="pu-checks">
      {tenants.map((tn) => {
        const on = selected.includes(tn.tenantId);
        return (
          <button key={tn.tenantId} type="button" role="checkbox" aria-checked={on} className={`pu-ck${on ? ' is-on' : ''}`} onClick={() => onToggle(tn.tenantId)} disabled={disabled}>
            <i>{on && <Svg name="check" size={11} />}</i>
            {tn.name}
          </button>
        );
      })}
    </div>
  );
}

// A pop-up: header with an icon or initials, body, and a beige foot with the actions.
function Modal({ size = 'm', head, onClose, busy, children }) {
  return (
    <div className="pu-overlay" onClick={() => !busy && onClose()}>
      <div className={`pu-modal pu-modal--${size}`} role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pu-mh">
          {head}
          <button type="button" className="pu-x" onClick={onClose} aria-label="Luk" disabled={busy}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// tenantScope: shown on one customer's page (only that customer's people, with their own invite and add). scopeOrgId: the organisation
// that customer belongs to, so a NordComply provider can pick among its customers.
export default function Users({ tenantScope = null, tenantLabel = '', scopeOrgId = null } = {}) {
  const { role: viewerRole, orgName: viewerOrgName, user: viewer, isOwner } = useAuth();
  const isProvider = viewerRole === 'provider';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Drilled in from Organisationer → this Brugere page is scoped to one
  // MSP already, so there's nothing to pick — the org is implicit.
  const viewingOrgId = searchParams.get('org');
  // The Provider Portal's own unscoped Brugere page — NordComply's own
  // team, kept structurally separate from any org's Tenants portal (where
  // admins/kunder are managed instead). See handleListUsers's matching
  // backend filter.
  const providerOnlyView = isProvider && !viewingOrgId && !tenantScope;
  // NordComply's own team, or every customer-side person (the provider's second view).
  const [view, setView] = useState('team');
  const customersView = providerOnlyView && view === 'customers';
  // An organisation's own admins: its team.
  const teamView = !providerOnlyView && !tenantScope;
  const [users, setUsers] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const showLoading = !users && !error;
  const [filter, setFilter] = useState('all');
  const [lockingEmail, setLockingEmail] = useState('');

  const [inviteOpen, setInviteOpen] = useState(false);
  // On a customer's page a new person is a customer user of that customer (set when the pop-up opens).
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('customer');
  const [authMethod, setAuthMethod] = useState('microsoft');
  const [tenantIds, setTenantIds] = useState([]);
  const [orgId, setOrgId] = useState(viewingOrgId || '');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  // Reset of a local user's password / MFA: { user, reset } while confirming.
  const [resetTarget, setResetTarget] = useState(null);
  const [resetNote, setResetNote] = useState(null);

  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editJobTitle, setEditJobTitle] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [editRole, setEditRole] = useState('customer');
  // Whether the person may make decisions: only NordComply sees and sets this.
  const [canDecide, setCanDecide] = useState(true);
  const [editCanDecide, setEditCanDecide] = useState(true);
  // The rights of a NordComply team member: only the owner sees and sets them.
  const [editRights, setEditRights] = useState([]);
  const [editTenantIds, setEditTenantIds] = useState([]);
  const [editOrgId, setEditOrgId] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Shown under "Om rollerne" for Admin — the org an admin's access is
  // scoped to. A provider drilling in from Organisationer already knows
  // which org (orgName search param); an admin viewing their own org's
  // Brugere page directly gets it from /api/me instead.
  const currentOrgName = searchParams.get('orgName') || viewerOrgName || null;

  useEffect(() => {
    loadUsers();
    apiFetch(`/api/tenants${viewingOrgId || scopeOrgId ? `?orgId=${viewingOrgId || scopeOrgId}` : ''}`).then((r) => r.json()).then((data) => {
      if (Array.isArray(data)) {
        setTenants(data);
      }
    }).catch(() => {});
    if (isProvider && !viewingOrgId && !tenantScope) {
      listOrganizations().then((data) => {
        if (Array.isArray(data.organizations)) {
          setOrgs(data.organizations);
          if (data.organizations.length > 0) setOrgId(data.organizations[0].id);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingOrgId, view, tenantScope]);

  useEffect(() => {
    if (teamView) setRole('admin');
    if (providerOnlyView) setRole('provider');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerOnlyView, teamView]);

  async function confirmReset() {
    const { user: target, reset } = resetTarget;
    const result = await resetUserCredentials(target.email, reset);
    if (!result.success) throw new Error(result.error || 'Kunne ikke nulstille adgangen.');
    setResetTarget(null);
    setResetNote(
      result.emailSent
        ? { ok: true, text: `Nulstillet. ${target.email} har fået en mail med et link.` }
        : { ok: false, text: 'Adgangen er nulstillet, men mailen kunne ikke sendes. Prøv at nulstille igen om lidt.' }
    );
    // Refresh the list and the open user so the status line is right.
    await loadUsers();
    if (result.user) setEditingUser((cur) => (cur ? { ...cur, ...result.user } : cur));
  }

  function toggleTenant(list, setList, id) {
    setList(list.includes(id) ? list.filter((t) => t !== id) : [...list, id]);
  }

  // Someone who has never signed in may not have got the first mail: it can be sent again (a password account gets a new link).
  const [mailingEmail, setMailingEmail] = useState('');
  const [mailNote, setMailNote] = useState(null);
  const canResend = (u) => !u.lastLoginAt && String(u.email).toLowerCase() !== String(viewer?.email || '').toLowerCase() && (viewerRole === 'provider' || (viewerRole === 'admin' && u.role === 'customer'));
  async function handleResend(u) {
    setMailingEmail(u.email);
    setMailNote(null);
    try {
      const result = await resendWelcomeMail(u.email);
      if (!result.success) throw new Error(result.error || 'Kunne ikke sende mailen.');
      setMailNote(result.emailSent
        ? { ok: true, text: `Velkomstmailen er sendt til ${u.email}.${u.authMethod === 'local' ? ' Det gamle link virker ikke mere.' : ''}` }
        : { ok: false, text: 'Mailen kunne ikke sendes lige nu. Prøv igen om lidt.' });
    } catch (err) {
      setMailNote({ ok: false, text: err.message });
    } finally {
      setMailingEmail('');
    }
  }

  const [approvingEmail, setApprovingEmail] = useState('');
  // Who may activate is decided by the backend; this only decides whether to show the button.
  const canApprove = (u) => String(u.email).toLowerCase() !== String(viewer?.email || '').toLowerCase() && (viewerRole === 'provider' || (viewerRole === 'admin' && u.role === 'customer'));
  async function handleApprove(u) {
    setApprovingEmail(u.email);
    setError('');
    try {
      await approveUser(u.email);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setApprovingEmail('');
    }
  }

  const canLock = (u) => String(u.email).toLowerCase() !== String(viewer?.email || '').toLowerCase() && (viewerRole === 'provider' || (viewerRole === 'admin' && u.role === 'customer'));
  async function handleLock(u, lock) {
    setLockingEmail(u.email);
    setEditError('');
    try {
      await lockUser(u.email, lock);
      await loadUsers();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setLockingEmail('');
    }
  }

  async function loadUsers() {
    try {
      const url = tenantScope ? `/api/users?tenantId=${tenantScope}${isProvider && scopeOrgId ? `&orgId=${scopeOrgId}` : ''}` : customersView ? '/api/users?scope=customers' : `/api/users${viewingOrgId ? `?orgId=${viewingOrgId}` : ''}`;
      const res = await apiFetch(url);
      const data = await res.json();
      if (!Array.isArray(data.users)) throw new Error(data.error || 'Data ikke tilgængelig');
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    }
  }

  function closeInvite() {
    if (inviting) return;
    setInviteOpen(false);
    setInviteError('');
  }

  async function handleInvite(e) {
    e.preventDefault();
    setInviteError('');
    if (tenantScope && !tenantIds.includes(tenantScope)) setTenantIds([tenantScope]);
    if (!name.trim() || !email.trim()) {
      setInviteError('Udfyld navn og email');
      return;
    }
    if (role === 'customer' && tenantIds.length === 0 && !tenantScope) {
      setInviteError('Vælg mindst én kunde');
      return;
    }
    if (role === 'admin' && isProvider && !orgId && !tenantScope) {
      setInviteError('Vælg en organisation');
      return;
    }
    setInviting(true);
    try {
      const res = await apiFetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          jobTitle: jobTitle.trim(),
          email: email.trim(),
          role,
          tenantIds: role === 'customer' ? (tenantScope ? [...new Set([...tenantIds, tenantScope])] : tenantIds) : [],
          ...(role === 'customer' || role === 'admin' ? { authMethod: authMethod === 'local' ? 'local' : undefined } : {}),
          ...(role === 'admin' && isProvider ? { orgId } : {}),
          ...(isProvider && role !== 'provider' ? { canDecide } : {}),
        }),
      });
      if (!res.ok) throw new Error('Kunne ikke oprette bruger');
      setName('');
      setJobTitle('');
      setEmail('');
      setAuthMethod('microsoft');
      setTenantIds([]);
      setCanDecide(true);
      setInviteOpen(false);
      await loadUsers();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviting(false);
    }
  }

  function openEdit(u) {
    setResetNote(null);
    setEditingUser(u);
    setEditName(u.name || '');
    setEditJobTitle(u.jobTitle || '');
    setEditRole(u.role);
    setEditCanDecide(u.canDecide !== false);
    setEditRights(Array.isArray(u.permissions) ? u.permissions : []);
    setEditTenantIds(u.tenantIds || []);
    setEditOrgId(u.orgId || (isProvider ? '' : orgId));
    setEditError('');
  }

  async function saveEdit(e) {
    e.preventDefault();
    setEditError('');
    if (editRole === 'customer' && editTenantIds.length === 0) {
      setEditError('Vælg mindst én tenant');
      return;
    }
    if (editRole === 'admin' && isProvider && !editOrgId) {
      setEditError('Vælg en organisation');
      return;
    }
    setEditSaving(true);
    try {
      const res = await apiFetch('/api/users', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: editingUser.email,
          name: editName.trim(),
          jobTitle: editJobTitle.trim(),
          role: editRole,
          tenantIds: editRole === 'customer' ? editTenantIds : [],
          ...(editRole === 'admin' && isProvider ? { orgId: editOrgId } : {}),
          ...(isProvider && editRole !== 'provider' ? { canDecide: editCanDecide } : {}),
          ...(isOwner && editRole === 'provider' && !editingUser.isOwner ? { permissions: editRights } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Kunne ikke gemme ændringer');
      }
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  }

  async function confirmRemove() {
    if (tenantScope) {
      const u = (users || []).find((x) => x.email === removeTarget);
      const rest = (u?.tenantIds || []).filter((id) => id !== tenantScope);
      if (u && rest.length > 0) {
        const res = await apiFetch('/api/users', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: u.email, role: 'customer', tenantIds: rest }) });
        if (!res.ok) throw new Error('Kunne ikke fjerne adgangen');
        setRemoveTarget(null);
        await loadUsers();
        return;
      }
    }
    const res = await apiFetch(`/api/users?email=${encodeURIComponent(removeTarget)}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Kunne ikke fjerne bruger');
    setRemoveTarget(null);
    await loadUsers();
  }

  const tenantNameById = {};
  tenants.forEach((t) => { tenantNameById[t.tenantId] = t.name; });
  function tenantAccessLabel(u) {
    if (u.role === 'provider') return 'Alle organisationer';
    if (u.role === 'admin') return 'Alle tenants';
    if (!u.tenantIds || u.tenantIds.length === 0) return '—';
    // A single tenant is named (unambiguous, short); more than one is just a count on the list row —
    // naming them all truncated to "Global-Aworks.com, Northwin…" was both unreadable and revealed
    // nothing useful at a glance. The full, real list is one click away in Rediger bruger.
    if (u.tenantIds.length === 1) return tenantNameById[u.tenantIds[0]] || u.tenantIds[0];
    return `${u.tenantIds.length} tenants`;
  }

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || [])
      .filter((u) => (tenantScope ? u.role === 'customer' : teamView ? u.role === 'admin' : true))
      .filter((u) => filter === 'all' || (filter === 'customer' ? u.role === 'customer' : u.role !== 'customer'))
      .filter((u) => !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      ;
  }, [users, search, filter, tenantScope, teamView]);

  const orgName = searchParams.get('orgName');
  const [sort, toggleSort] = useSort('user');
  const STATUS_RANK = { locked: 0, pending: 1, invited: 2, active: 3 };
  // Seconds as hours and minutes: 3 t 20 min, 45 min, 0 min.
  const hoursText = (sec) => { const m = Math.round((Number(sec) || 0) / 60); return m >= 60 ? `${Math.floor(m / 60)} t ${m % 60} min` : `${m} min`; };
  const sortedUsers = sortBy([...sorted].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'da')), sort, {
    user: (u) => u.name,
    access: (u) => tenantAccessLabel(u),
    org: (u) => u.orgName,
    kunde: (u) => (u.role === 'admin' ? 'Alle kunder' : (u.tenantNames || []).join(', ')),
    role: (u) => u.role,
    login: (u) => (u.lastLoginAt ? new Date(u.lastLoginAt).getTime() : null),
    status: (u) => STATUS_RANK[u.status] ?? 3,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [addingEmail, setAddingEmail] = useState('');
  async function openAdd() {
    setAddOpen(true);
    setCandidates(null);
    try {
      const res = await apiFetch(`/api/users${isProvider && scopeOrgId ? `?orgId=${scopeOrgId}` : ''}`);
      const data = await res.json();
      setCandidates((data.users || []).filter((u) => u.role === 'customer' && !(u.tenantIds || []).includes(tenantScope)));
    } catch {
      setCandidates([]);
    }
  }
  async function addExisting(u) {
    setAddingEmail(u.email);
    try {
      const res = await apiFetch('/api/users', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: u.email, role: 'customer', tenantIds: [...(u.tenantIds || []), tenantScope] }) });
      if (!res.ok) throw new Error('Kunne ikke tilføje adgangen');
      setAddOpen(false);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingEmail('');
    }
  }
  const kundeLabel = (u) => (u.role === 'admin' ? 'Alle kunder' : (u.tenantNames || []).length === 0 ? '—' : u.tenantNames.length === 1 ? u.tenantNames[0] : `${u.tenantNames.length} kunder`);
  const rowClass = customersView ? 'pu-tr--uc' : tenantScope ? 'pu-tr--m' : 'pu-tr--u';
  const counts = { all: (users || []).length, customer: (users || []).filter((u) => u.role === 'customer').length, admin: (users || []).filter((u) => u.role !== 'customer').length };
  const STATUS = { active: ['Aktiv', 'ok'], pending: ['Afventer', 'warn'], invited: ['Inviteret', ''], locked: ['Låst', 'bad'] };
  const shownUser = editingUser ? (users || []).find((x) => x.email === editingUser?.email) || editingUser : null;

  return (
    <>
      <div className={tenantScope ? 'pu-embed' : 'pu-page'}>
        {orgName && !tenantScope && (
          <button type="button" className="pu-back" onClick={() => navigate(`/tenants?org=${viewingOrgId}&orgName=${encodeURIComponent(orgName)}`)}>
            <Svg name="back" size={13} />
            {orgName}
          </button>
        )}
        {tenantScope ? (
          <div className="pu-head pu-head--sub">
            <div>
              <h3>Brugere med adgang til {tenantLabel || 'kunden'}</h3>
              <p>Her administreres kundens brugere. Organisationens team har adgang til alle kunder og står under Team.</p>
            </div>
            <span className="pu-btns">
              <button type="button" className="pu-btn" onClick={openAdd}><Svg name="user" />Tilføj eksisterende</button>
              <button type="button" className="pu-btn pu-btn--p" onClick={() => setInviteOpen(true)}><Svg name="mail" />Inviter bruger</button>
            </span>
          </div>
        ) : (
          <div className="pu-head">
            <div>
              <h1>{teamView ? `${currentOrgName || ''} Team`.trim() : 'Brugere'}</h1>
              <p>{providerOnlyView ? (customersView ? 'Alle kunders brugere, på tværs af organisationer.' : 'NordComplys eget team.') : `Personerne i ${currentOrgName || 'jeres organisation'}, som har adgang til alle kunder.`}</p>
            </div>
            {!customersView && <button type="button" className="pu-btn pu-btn--p" onClick={() => setInviteOpen(true)}><Svg name="mail" />Inviter bruger</button>}
          </div>
        )}
        {providerOnlyView && (
          <div className="pu-tabs">
            <button type="button" className={view === 'team' ? 'is-on' : ''} onClick={() => { setUsers(null); setView('team'); }}>NordComply-team</button>
            <button type="button" className={view === 'customers' ? 'is-on' : ''} onClick={() => { setUsers(null); setView('customers'); }}>Kundebrugere</button>
          </div>
        )}

        {error && <p className="state-error">{error}</p>}
        {mailNote && <p className={`pu-msg ${mailNote.ok ? 'pu-msg--ok' : 'pu-msg--bad'}`}>{mailNote.text}</p>}
        {showLoading && <LoadingState />}

        {!showLoading && users && (
          users.length === 0 ? (
            <div className="pu-empty">
              <p>Ingen brugere endnu</p>
              <button type="button" className="pu-btn pu-btn--p" onClick={() => setInviteOpen(true)}>Inviter bruger</button>
            </div>
          ) : (
            <>
              <div className="pu-bar">
                <SearchInput value={search} onChange={setSearch} placeholder="Søg efter bruger..." />
                {customersView && (
                  <div className="pu-seg" role="group" aria-label="Vis">
                    <button type="button" className={filter === 'all' ? 'is-on' : ''} onClick={() => setFilter('all')}>Alle<u>{counts.all}</u></button>
                    <button type="button" className={filter === 'customer' ? 'is-on' : ''} onClick={() => setFilter('customer')}>Kunder<u>{counts.customer}</u></button>
                    <button type="button" className={filter === 'admin' ? 'is-on' : ''} onClick={() => setFilter('admin')}>Admins<u>{counts.admin}</u></button>
                  </div>
                )}
              </div>
              {sorted.length === 0 ? (
                <div className="pu-empty"><p>{search.trim() ? 'Ingen brugere matcher din søgning' : 'Ingen brugere her endnu'}</p></div>
              ) : (
                <div className="pu-table">
                  <div className={`pu-th pu-tr ${rowClass}`}>
                    <SortHead label="Bruger" k="user" sort={sort} onSort={toggleSort} />{customersView ? <><SortHead label="Organisation" k="org" sort={sort} onSort={toggleSort} /><SortHead label="Kunde" k="kunde" sort={sort} onSort={toggleSort} /></> : !tenantScope && <SortHead label="Adgang" k="access" sort={sort} onSort={toggleSort} />}<SortHead label="Rolle" k="role" sort={sort} onSort={toggleSort} /><SortHead label="Seneste login" k="login" sort={sort} onSort={toggleSort} /><SortHead label="Status" k="status" sort={sort} onSort={toggleSort} /><span />
                  </div>
                  {sortedUsers.map((u) => {
                    const [stText, stTone] = STATUS[u.status] || STATUS.active;
                    return (
                      <div
                        key={u.email}
                        className={`pu-tr ${rowClass} pu-tr--click`}
                        role="button"
                        tabIndex={0}
                        onClick={() => openEdit(u)}
                        onKeyDown={(e) => { if (e.key === 'Enter') openEdit(u); }}
                      >
                        <span className="pu-who">
                          <span className="pu-av">{initials(u.name || u.email)}</span>
                          <span>
                            <b>{u.name}</b>
                            <small>{u.jobTitle ? `${u.jobTitle} · ${u.email}` : u.email}</small>
                            {u.notif && (
                              <small className="pu-notif" title={`Notifikationer slået til${u.notif.since ? ` ${formatDateTime(u.notif.since)}` : ''}`}>
                                <NcIcon name="bell" size={11} />
                                {u.notif.frequency === 'weekly' ? 'Hver uge' : 'Hver dag'} · {[u.notif.types.bad && 'Kræver handling', u.notif.types.warn && 'Bør gennemgås'].filter(Boolean).join(' + ')}
                              </small>
                            )}
                            {canResend(u) && (
                              <button type="button" className="pu-mail" title="Send velkomstmail igen" aria-label="Send velkomstmail igen" disabled={mailingEmail === u.email} onClick={(e) => { e.stopPropagation(); handleResend(u); }}>
                                <PortalIcon name="mail" size={12} />Send velkomstmail
                              </button>
                            )}
                          </span>
                        </span>
                        {customersView ? <><span className="pu-c">{u.orgName || '—'}</span><span className="pu-c">{kundeLabel(u)}</span></> : !tenantScope && <span className="pu-c" title={tenantAccessLabel(u)}>{tenantAccessLabel(u)}</span>}
                        <span className="pu-roles"><span className={`pu-chip ${u.role === 'customer' ? 'pu-chip--neu' : 'pu-chip--adm'}`}>{ROLE_LABEL[u.role] || u.role}</span>{u.isOwner && <span className="pu-chip pu-chip--new">Ejer</span>}{u.role !== 'provider' && u.canDecide === false && <span className="pu-chip pu-chip--warn">Kun læse</span>}</span>
                        <span className="pu-c pu-c--m">{formatDateTime(u.lastLoginAt)}{isOwner && u.lastActiveAt !== undefined && <small style={{ display: 'block', color: 'var(--ink-500)' }} title="Kun synligt for ejeren">Aktiv: {hoursText(u.activeSeconds7)} (7 d) · {hoursText(u.activeSeconds30)} (30 d){u.activeSecondsAll !== undefined ? ` · ${hoursText(u.activeSecondsAll)} i alt${u.activeSince ? ` siden ${new Date(`${u.activeSince}T12:00:00`).toLocaleDateString('da-DK', { dateStyle: 'medium' })}` : ''}` : ''}{u.lastActiveAt ? ` · sidst ${formatDateTime(u.lastActiveAt)}` : ''}</small>}</span>
                        <span><span className={`pu-st${stTone ? ` pu-st--${stTone}` : ''}`}><i />{stText}</span></span>
                        <span className="pu-acts">
                          {u.status === 'pending' && canApprove(u) && (
                            <button type="button" className="pu-btn pu-btn--p" style={{ padding: '4px 11px', fontSize: 12 }} disabled={approvingEmail === u.email} onClick={(e) => { e.stopPropagation(); handleApprove(u); }}>
                              {approvingEmail === u.email ? 'Aktiverer…' : 'Aktivér'}
                            </button>
                          )}
                          <button type="button" className="pu-ib" title="Redigér bruger" aria-label="Redigér bruger" onClick={(e) => { e.stopPropagation(); openEdit(u); }}><PortalIcon name="edit" /></button>
                          <button type="button" className="pu-ib pu-ib--del" title="Fjern bruger" aria-label="Fjern bruger" onClick={(e) => { e.stopPropagation(); setRemoveTarget(u.email); }}><PortalIcon name="trash" /></button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="pu-foot">Klik på en bruger for at redigere. Nye brugere skal aktiveres, før de kan logge ind.</p>
            </>
          )
        )}
      </div>

      {inviteOpen && (
        <Modal
          size="m"
          onClose={closeInvite}
          busy={inviting}
          head={(
            <>
              <span className="pu-ico"><PortalIcon name="org" size={20} /></span>
              <div><h2>Inviter bruger</h2><small>Personen får en mail og kan logge ind, når kontoen er aktiveret.</small></div>
            </>
          )}
        >
          <form onSubmit={handleInvite}>
            <div className="pu-mb">
              <div className="pu-grid2">
                <div className="pu-col">
                  <div className="pu-f"><label>Navn</label><input value={name} onChange={(e) => setName(e.target.value)} disabled={inviting} autoFocus /></div>
                  <div className="pu-f"><label>Stilling <em>(valgfrit)</em></label><input value={jobTitle} maxLength={60} placeholder="Fx IT Ansvarlig" onChange={(e) => setJobTitle(e.target.value)} disabled={inviting} /></div>
                  <div className="pu-f"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="navn@firma.dk" disabled={inviting} /></div>
                  {!tenantScope && (
                  <div className="pu-f">
                    <span className="pu-lab">Rolle</span>
                    {providerOnlyView ? (
                      <Segmented value="provider" options={[['provider', 'NordComply']]} onChange={() => {}} disabled />
                    ) : teamView ? (
                      <Segmented value="admin" options={[['admin', 'MSP-admin']]} onChange={() => {}} disabled />
                    ) : (
                      <Segmented value={role} options={[['customer', 'Kundebruger'], ['admin', 'MSP-admin']]} onChange={setRole} disabled={inviting} />
                    )}
                  </div>
                  )}
                  {isProvider && role !== 'provider' && (
                    <div className="pu-f">
                      <span className="pu-lab">Beslutninger</span>
                      <Segmented value={canDecide ? 'yes' : 'no'} options={[['yes', 'Må træffe'], ['no', 'Kun læse']]} onChange={(k) => setCanDecide(k === 'yes')} disabled={inviting} />
                      {!canDecide && <em>Kan se alt, men ikke træffe beslutninger, og vises ikke som ejer.</em>}
                    </div>
                  )}
                </div>
                <div className="pu-col">
                  {tenantScope && (
                    <div className="pu-info"><b>Kundebruger</b><span>Får adgang til {tenantLabel || 'kunden'}. Flere kunder kan gives bagefter fra den enkelte kundes side.</span></div>
                  )}
                  {role === 'customer' && !tenantScope && (
                    <div className="pu-f"><span className="pu-lab">Kunder</span><TenantChecklist tenants={tenants} selected={tenantIds} onToggle={(id) => toggleTenant(tenantIds, setTenantIds, id)} disabled={inviting} /></div>
                  )}
                  {role === 'admin' && !providerOnlyView && (
                    <div className="pu-info"><b>Admin</b><span>Adgang til alle kunder under {currentOrgName ? currentOrgName : 'jeres organisation'}, ikke andre organisationers.</span></div>
                  )}
                  {providerOnlyView && (
                    <div className="pu-info"><b>Provider</b><span>NordComplys eget team. Ser og administrerer alt. Kunde- og admin-adgang oprettes i den enkelte organisations Tenants-portal.</span></div>
                  )}
                  {(role === 'customer' || role === 'admin') && (
                    <div className="pu-f"><span className="pu-lab">Login</span><span className="pu-ro">Microsoft 365</span></div>
                  )}
                </div>
              </div>
              {inviteError && <p className="pu-msg pu-msg--bad">{inviteError}</p>}
            </div>
            <div className="pu-mf">
              <span className="pu-sp" />
              <button type="button" className="pu-btn" onClick={closeInvite} disabled={inviting}>Annullér</button>
              <button type="submit" className="pu-btn pu-btn--p" disabled={inviting}>{inviting ? 'Tilføjer…' : 'Inviter bruger'}</button>
            </div>
          </form>
        </Modal>
      )}

      {addOpen && (
        <Modal
          size="s"
          onClose={() => setAddOpen(false)}
          busy={!!addingEmail}
          head={(
            <>
              <span className="pu-ico"><PortalIcon name="org" size={20} /></span>
              <div><h2>Tilføj eksisterende bruger</h2><small>Giv en, der allerede har adgang til andre kunder, adgang til {tenantLabel || 'denne kunde'}.</small></div>
            </>
          )}
        >
          <div className="pu-mb">
            {candidates === null && <p className="pu-msg">Henter…</p>}
            {candidates && candidates.length === 0 && <p className="pu-msg">Der er ingen andre kundebrugere at tilføje. Brug Inviter bruger til en ny.</p>}
            {candidates && candidates.length > 0 && (
              <div className="pu-pick">
                {candidates.map((u) => (
                  <div className="pu-pick__r" key={u.email}>
                    <span className="pu-who"><span className="pu-av">{initials(u.name || u.email)}</span><span><b>{u.name}</b><small>{u.jobTitle ? `${u.jobTitle} · ${u.email}` : u.email}</small></span></span>
                    <button type="button" className="pu-btn pu-btn--sm" disabled={addingEmail === u.email} onClick={() => addExisting(u)}>{addingEmail === u.email ? 'Tilføjer…' : 'Tilføj'}</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {resetTarget && (
        <ConfirmDialog
          title={RESET_LABEL[resetTarget.reset].title}
          message={`${resetTarget.user.name || resetTarget.user.email} (${resetTarget.user.email}) logges ud alle steder, og ${RESET_LABEL[resetTarget.reset].what} virker ikke længere. Brugeren får en mail med et engangslink, der virker i 24 timer. I ser aldrig adgangskoden eller koden.`}
          confirmLabel="Nulstil"
          onCancel={() => setResetTarget(null)}
          onConfirm={confirmReset}
        />
      )}

      {removeTarget && (
        <ConfirmDialog
          title={tenantScope ? 'Fjern adgang' : 'Fjern bruger'}
          message={tenantScope && ((users || []).find((x) => x.email === removeTarget)?.tenantIds || []).length > 1 ? `Fjern ${removeTarget}s adgang til ${tenantLabel || 'kunden'}? Brugeren beholder adgang til sine andre kunder.` : `Fjern ${removeTarget} fra NordComply?`}
          confirmLabel="Fjern"
          onCancel={() => setRemoveTarget(null)}
          onConfirm={confirmRemove}
        />
      )}

      {editingUser && shownUser && (
        <Modal
          size="m"
          onClose={() => setEditingUser(null)}
          busy={editSaving}
          head={(
            <>
              <span className="pu-av pu-av--l">{initials(shownUser.name || shownUser.email)}</span>
              <div><h2>{shownUser.name || shownUser.email}</h2><small>{shownUser.email}</small></div>
            </>
          )}
        >
          <form onSubmit={saveEdit}>
            <div className="pu-mb">
              <div className="pu-grid2">
                <div className="pu-col">
                  <div className="pu-f"><label>Navn</label><input value={editName} onChange={(e) => setEditName(e.target.value)} disabled={editSaving} autoFocus /></div>
                  <div className="pu-f"><label>Stilling <em>(valgfrit)</em></label><input value={editJobTitle} maxLength={60} placeholder="Fx IT Ansvarlig" onChange={(e) => setEditJobTitle(e.target.value)} disabled={editSaving} /></div>
                  <div className="pu-f">
                    <span className="pu-lab">Rolle</span>
                    <Segmented
                      value={editRole}
                      options={[['customer', 'Kundebruger'], ['admin', 'MSP-admin'], ...(isProvider && !viewingOrgId ? [['provider', 'NordComply']] : [])]}
                      onChange={setEditRole}
                      disabled={editSaving}
                    />
                  </div>
                  {isProvider && editRole !== 'provider' && (
                    <div className="pu-f">
                      <span className="pu-lab">Beslutninger</span>
                      <Segmented value={editCanDecide ? 'yes' : 'no'} options={[['yes', 'Må træffe'], ['no', 'Kun læse']]} onChange={(k) => setEditCanDecide(k === 'yes')} disabled={editSaving} />
                      {!editCanDecide && <em>Kan se alt, men ikke træffe beslutninger, og vises ikke som ejer.</em>}
                    </div>
                  )}
                  {editRole === 'provider' && (editingUser.isOwner || isOwner) && (
                    <div className="pu-f">
                      <span className="pu-lab">Rettigheder</span>
                      {editingUser.isOwner ? (
                        <em>Ejer. Har alle rettigheder og er den eneste, der kan give dem.</em>
                      ) : (
                        <div className="pu-checks">
                          {NC_RIGHTS.map(([key, label]) => {
                            const on = editRights.includes(key);
                            return (
                              <button key={key} type="button" role="checkbox" aria-checked={on} className={`pu-ck${on ? ' is-on' : ''}`} disabled={editSaving} onClick={() => setEditRights(on ? editRights.filter((k) => k !== key) : [...editRights, key])}>
                                <i>{on && <Svg name="check" size={11} />}</i>{label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {editRole === 'admin' && isProvider && !viewingOrgId && (
                    <div className="pu-f">
                      <label>Organisation</label>
                      <select value={editOrgId} onChange={(e) => setEditOrgId(e.target.value)} disabled={editSaving}>
                        {orgs.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="pu-col">
                  {editRole === 'customer' && (
                    <div className="pu-f"><span className="pu-lab">Tenants</span><TenantChecklist tenants={tenants} selected={editTenantIds} onToggle={(id) => toggleTenant(editTenantIds, setEditTenantIds, id)} disabled={editSaving} /></div>
                  )}
                  <div className="pu-f"><span className="pu-lab">Login</span><span className="pu-ro">{shownUser.authMethod === 'local' ? 'Email og adgangskode' : 'Microsoft 365'}</span></div>
                  <div className="pu-f">
                    <span className="pu-lab">Konto</span>
                    <span className="pu-acc">
                      <span className={`pu-st pu-st--${(STATUS[shownUser.status] || STATUS.active)[1]}`}><i />{(STATUS[shownUser.status] || STATUS.active)[0]}</span>
                      <small>{shownUser.lastLoginAt ? `Senest logget ind ${formatDateTime(shownUser.lastLoginAt)}` : 'Aldrig logget ind'}</small>
                    </span>
                    <em>Aktivér slipper personen ind. Lås konto stopper adgangen med det samme, uden at slette noget.</em>
                  </div>
                </div>
              </div>
              {shownUser.authMethod === 'local' && shownUser.role !== 'provider' && shownUser.email.toLowerCase() !== String(viewer?.email || '').toLowerCase() && (
                <div className="pu-f">
                  <span className="pu-lab">Adgang (email og adgangskode)</span>
                  <em>Adgangskode: {shownUser.hasPassword ? 'sat' : 'ikke sat'} · MFA: {shownUser.hasMfa ? 'tilknyttet' : 'ikke tilknyttet'}</em>
                  <span className="pu-mf__a">
                    <button type="button" className="pu-link" onClick={() => setResetTarget({ user: shownUser, reset: 'password' })} disabled={editSaving}>Nulstil adgangskode</button>
                    <button type="button" className="pu-link" onClick={() => setResetTarget({ user: shownUser, reset: 'mfa' })} disabled={editSaving}>Nulstil MFA</button>
                    <button type="button" className="pu-link" onClick={() => setResetTarget({ user: shownUser, reset: 'both' })} disabled={editSaving}>Nulstil begge</button>
                  </span>
                  {resetNote && <p className={`pu-msg ${resetNote.ok ? 'pu-msg--ok' : 'pu-msg--bad'}`}>{resetNote.text}</p>}
                </div>
              )}
              {editError && <p className="pu-msg pu-msg--bad">{editError}</p>}
            </div>
            <div className="pu-mf">
              <span className="pu-mf__a">
                {canResend(shownUser) && <button type="button" className="pu-link" onClick={() => handleResend(shownUser)} disabled={mailingEmail === shownUser.email}>Send velkomstmail igen</button>}
                {shownUser.status === 'pending' && canApprove(shownUser) && <button type="button" className="pu-link" onClick={() => handleApprove(shownUser)} disabled={approvingEmail === shownUser.email}>{approvingEmail === shownUser.email ? 'Aktiverer…' : 'Aktivér'}</button>}
                {canLock(shownUser) && (shownUser.status === 'locked'
                  ? <button type="button" className="pu-link" onClick={() => handleLock(shownUser, false)} disabled={lockingEmail === shownUser.email}>Lås op</button>
                  : <button type="button" className="pu-link pu-link--bad" onClick={() => handleLock(shownUser, true)} disabled={lockingEmail === shownUser.email}>Lås konto</button>)}
              </span>
              <span className="pu-sp" />
              <button type="button" className="pu-btn" onClick={() => setEditingUser(null)} disabled={editSaving}>Annullér</button>
              <button type="submit" className="pu-btn pu-btn--p" disabled={editSaving}>{editSaving ? 'Gemmer…' : 'Gem ændringer'}</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
